import { useCallback } from 'react';

import snakeCase from 'es-toolkit/compat/snakeCase';

import { useTrackEvent } from '../useTrackEvent';

// ─────────────────────────────────────────────────────────────────────────────

export const useAdjustEvents = () => {
  const trackEvent = useTrackEvent();

  const trackAdjustLogin = useCallback(() => {
    trackEvent({ eventToken: LOGIN_EVENT_TOKEN, properties: {} });
  }, [trackEvent]);

  const trackAdjustRegistration = useCallback(() => {
    trackEvent({ eventToken: REGISTRATION_EVENT_TOKEN, properties: {} });
  }, [trackEvent]);

  const trackAdjustAddToCart = useCallback(
    (params: TrackAdjustAddToCartParams) => {
      trackEvent({ eventToken: ADD_TO_CART_EVENT_TOKEN, properties: snakeCaseKeys(params) });
    },
    [trackEvent],
  );

  const trackAdjustPurchase = useCallback(
    (params: TrackAdjustPurchaseParams) => {
      const { subtotalValue, ...rest } = params;

      trackEvent({
        eventToken: PURCHASE_EVENT_TOKEN,
        properties: snakeCaseKeys(rest),
        revenue: subtotalValue,
        deduplicationId: params.orderId,
      });
    },
    [trackEvent],
  );

  return {
    trackAdjustLogin,
    trackAdjustRegistration,
    trackAdjustAddToCart,
    trackAdjustPurchase,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const LOGIN_EVENT_TOKEN = 'xambxi';
const REGISTRATION_EVENT_TOKEN = 'cl2sar';
const ADD_TO_CART_EVENT_TOKEN = 'k5b6xv';
const PURCHASE_EVENT_TOKEN = 'wz40zy';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function snakeCaseKeys(input: Record<string, string | undefined>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(input)
      .filter((entry): entry is [string, string] => entry[1] !== undefined)
      .map(([key, value]) => [snakeCase(key), value]),
  );
}

// ─── Types ───────────────────────────────────────────────────────────────────

type TrackAdjustAddToCartParams = {
  restaurantId?: string;
  restaurantName?: string;
  productId?: string;
  productName?: string;
};

type TrackAdjustPurchaseParams = {
  orderId?: string;
  restaurantId?: string;
  subtotalValue: number;
};
